import { message } from 'antd';
import NotFoundError from '../errors/NotFoundError';
import HttpRequest from './HttpRequest';
import { MOCK_WEBSITE_PREVIEW_TEMPLATE } from '../data/mock-website-preview-template';
import UtmService from './UtmService';

interface SubcategoryItem {
  name: string;
  isCustom: boolean;
  keywords: string[];
}

interface RelatedItem {
  name: string;
  keywords: string[];
  subcategories: Omit<SubcategoryItem, 'isCustom'>[];
}

interface MainCategory {
  name: string;
  keywords: string[];
}

export interface SitemapParams {
  category: string;
  main: MainCategory;
  subcategories: SubcategoryItem[];
  related: RelatedItem[];
  languageCode: string;
}

export interface IBusinessSessionData {
  company_name: string;
  zip: string;
  address: string;
  phone: string;
  website: string;
  domain_type: string;
  category: string;
  subcategories: string[];
  related_categories: string[];
  external_service_appointments_tool: string;
  external_service_online_shop: string;
  external_service_table_reservations: string;
  external_service_delivery: string;
  sitemap?: any;
  important_pages: any[];
  business_description: string;
  business_founding_date: string;
  business_employees: number;
  business_client_addressing: string;
  business_about_yourself: string;
  business_tone_of_voice: string;
  business_url_facebook: string;
  business_url_instagram: string;
  business_url_pinterest: string;
  business_url_linkedin: string;
  business_url_tiktok: string;
  business_url_x: string;
  business_url_xing: string;
  business_url_youtube: string;
  logo: string;
  font_display: string;
  font_main: string;
  templates_data?: any[];
  colorMap: any;
}

export interface SocialMediaProfilesResponse {
  status: 'success' | 'error';
  data: {
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    facebook?: string;
    xing?: string;
    youtube?: string;
    tiktok?: string;
    pinterest?: string;
  };
}

class Api {
  async findBusinessOnGoogle(businessName: string) {
    try {
      return await HttpRequest.makeGetJSONRequest('/api/places', {
        searchText: businessName
      });
    } catch (error) {
      message.error('Error while fetching business from Google');
      throw error;
    }
  }

  async findBusinessOnGMB(businessName: string) {
    try {
      const resp = await HttpRequest.makeGetJSONRequest('/api/scrape/google-maps', {
        searchText: businessName
      });

      if (resp && resp.success) {
        return { status: 'success', data: resp.results };
      }

      return { status: 'error', data: [] };
    } catch (error) {
      message.error('Error while fetching business from Google');
      throw error;
    }
  }

  async findCategoryName(category: string): Promise<{
    status: string;
    data: Array<{
      id: number;
      name: string;
      subcategories: string[];
    }>;
  }> {
    try {
      return await HttpRequest.makeGetJSONRequest('/api/categories', {
        name: category
      });
    } catch (error) {
      message.error('Error while fetching category name');
      throw error;
    }
  }

  async findSubcategoryInfo(category: string): Promise<{
    status: string;
    data: {
      subcategories: any[];
      related: any[];
      main: any;
    };
  }> {
    try {
      let result = await HttpRequest.makeGetJSONRequest(
        '/api/categories/details',
        {
          name: category
        }
      );

      return result;
    } catch (error) {
      if (error instanceof NotFoundError) {
        return {
          status: 'not_found',
          data: {
            subcategories: [],
            related: [],
            main: null
          }
        };
      }

      message.error('Error while fetching subcategory info');
      throw error;
    }
  }

  async getSiteMapForExistingBusiness(websiteDomain: string) {
    try {
      let result = await HttpRequest.makeGetJSONRequest('/api/scrape/sitemap', {
        website: websiteDomain
      });

      return result.data;
    } catch (error) {
      if (error instanceof NotFoundError) {
        return {
          pages: [],
          images: []
        };
      }

      message.error('Error while fetching website sitemap');
      throw error;
    }
  }

  async getSitemap(params: SitemapParams) {
    try {
      const data = HttpRequest.makePostJSONRequest('/api/pages/sitemap', {
        category: params.category,
        main: params.main,
        subcategories: params.subcategories,
        related: params.related,
        languageCode: params.languageCode
      });

      return data;
    } catch (error) {
      message.error('Error while fetching website sitemap');
      throw error;
    }
  }

  async getTemplateData(
    category: string,
    data: any[],
    session: IBusinessSessionData
  ) {
    try {
      if (process.env.USE_CACHE_FOR_FAST_TESTING === 'true') {
        return MOCK_WEBSITE_PREVIEW_TEMPLATE;
      }

      return await HttpRequest.makePostJSONRequest(
        '/api/website-preview/template',
        {
          category: category,
          sitemap: data,
          session
        }
      );
    } catch (error) {
      message.error('Error while fetching template data');
      throw error;
    }
  }

  async setAppointmentSitemap(appointmentId: number, data: any) {
    try {
      return await HttpRequest.makePostJSONRequest(
        '/api/doku/setAppointmentSitemap',
        {
          appointmentId,
          ...data
        }
      );
    } catch (error) {
      message.error('Error while saving appointment sitemap');
      throw error;
    }
  }

  async saveSessionData(data: IBusinessSessionData): Promise<{
    data: {
      id: string;
      setup_identifier: string;
      redirect_url: string;
    };
  }> {
    try {
      return await HttpRequest.makePostJSONRequest('/api/session/save', data);
    } catch (error) {
      message.error('Error while saving session data');
      throw error;
    }
  }

  async extractWebsiteLogo(
    websiteUrl: string,
    category: string
  ): Promise<{
    success: boolean;
    errorMessage?: string;
    possibleImages: Array<{
      ObjectURL: string;
      PresignedUrl: string;
      colors?: any;
      width: number;
      height: number;
    }>;
  }> {
    return await HttpRequest.makeGetJSONRequest(
      '/api/scrape/website-logo-pictures',
      {
        website: websiteUrl,
        category
      }
    );
  }

  async scrapeGooglePlaces(place_id: string): Promise<{
    category: string;
    errorMessage: string;
    success: boolean;
  }> {
    return await HttpRequest.makeGetJSONRequest('/api/scrape/google-places', {
      place_id
    });

    //return {
    //  category: null,
    //  errorMessage: '',
    //  success: true
    //};
  }

  async uploadLogo(formData: FormData) {
    try {
      return await HttpRequest.makePostFormDataRequest(
        'api/upload/logo',
        formData
      );
    } catch (error) {
      message.error('Error while uploading logo');
      throw error;
    }
  }

  async uploadFile(formData: FormData) {
    try {
      return await HttpRequest.makePostFormDataRequest(
        'api/upload/file',
        formData
      );
    } catch (error) {
      message.error('Error while uploading file');
      throw error;
    }
  }

  async getThemeVariables(
    category: string,
    colorMap: any,
    fontMap: {
      display: { name: string };
      main: { name: string };
    }
  ): Promise<{
    data: {
      theme: Record<string, string>;
    };
  }> {
    return await HttpRequest.makePostJSONRequest(
      'api/colors/get-theme-variables',
      {
        category,
        colorMap,
        fontMap
      }
    );
  }

  async getColorConfig(
    primaryColor: string,
    category: string
  ): Promise<{
    data: {
      colors: any;
    };
  }> {
    return await HttpRequest.makeGetJSONRequest(
      '/api/colors/get-color-config',
      {
        color: primaryColor,
        category
      }
    );
  }

  async generateColors(category: string): Promise<{
    status: 'success' | 'error';
    data: {
      colors: any;
    };
  }> {
    return await HttpRequest.makeGetJSONRequest(
      '/api/colors/generate_primary_secondary_colors',
      {
        category
      }
    );
  }

  async searchStockImages(query: string) {
    return await HttpRequest.makeGetJSONRequest('api/pictures/search', {
      query
    });
  }

  async getStockImageForPreview(mediaId: string): Promise<{
    data: {
      ObjectURL: string;
      PresignedUrl: string;
    };
  }> {
    return await HttpRequest.makeGetJSONRequest('api/pictures/download', {
      id: mediaId
    });
  }

  async saveUserPreviewData(previewData: Record<string, any>) {
    return await HttpRequest.makePostJSONRequest(
      '/api/session/save_preview_data',
      {
        previewData
      }
    );
  }

  async regenerateText(
    areaName: string,
    resourceType: 'page' | 'template',
    allAreasTexts: any,
    category: string,
    sitemapTree: any[],
    currentEditedPageId: string | number,
    session: IBusinessSessionData
  ): Promise<{
    data: {
      text: string;
    };
  }> {
    return await HttpRequest.makePostJSONRequest(
      '/api/website-preview/regenerate-area',
      {
        areaName,
        resourceType,
        allAreasTexts,
        category,
        sitemap: sitemapTree,
        currentEditedPageId,
        session
      }
    );
  }

  async regeneratePageResources(
    allAreasTexts: any,
    category: string,
    sitemap: any[],
    currentEditedPageId: string | number,
    session: IBusinessSessionData
  ): Promise<{
    data: {
      texts: { name: string; text: string }[];
      images: { name: string; url: string }[];
    };
  }> {
    return await HttpRequest.makePostJSONRequest(
      '/api/website-preview/regenerate-page-resources',
      {
        allAreasTexts,
        category,
        sitemap,
        currentEditedPageId,
        session
      }
    );
  }

  async validateWebsite(websiteUrl: string): Promise<{
    status: string;
    data: {
      valid: boolean;
    };
  }> {
    try {
      return await HttpRequest.makeGetJSONRequest(
        '/api/places/validateWebsite',
        {
          url: websiteUrl
        }
      );
    } catch (error) {
      message.error('Error while validating website URL');
      throw error;
    }
  }

  async getWebsiteData(params: {
    website: string;
    companyName: string;
    country: string;
  }): Promise<any> {
    try {
      return await HttpRequest.makeGetJSONRequest('/api/scrape/website_data', {
        website: params.website,
        companyName: params.companyName,
        country: params.country
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        return {
          pages: [],
          images: [],
          businessDescription: ''
        };
      }

      message.error('Error while fetching website data');
      throw error;
    }
  }

  async generateBusinessDescription(params: {
    website?: string;
    city?: string;
    companyName: string;
    category: string;
  }): Promise<{
    status: 'success' | 'error';
    data: {
      businessDescription: string;
    };
  }> {
    try {
      return await HttpRequest.makeGetJSONRequest(
        '/api/places/generate_business_description',
        params
      );
    } catch (error) {
      message.error('Error while generating business description');
      throw error;
    }
  }

  async getKeywordsForCustomArea(
    areaName: string,
    category: string
  ): Promise<{
    status: 'success' | 'error';
    data: {
      keywords: string[];
    };
  }> {
    try {
      return await HttpRequest.makeGetJSONRequest(
        '/api/pages/get_keywords_for_custom_area',
        { name: areaName, category }
      );
    } catch (error) {
      message.error('Error while fetching keywords for custom area');
      throw error;
    }
  }

  async scrapeSocialMediaProfiles(
    params: {
      location: string;
      businessName: string;
      website: string;
    },
    signal?: AbortSignal
  ): Promise<SocialMediaProfilesResponse> {
    return await HttpRequest.makeGetJSONRequest(
      '/api/scrape/social-media-profiles',
      {
        location: params.location,
        businessName: params.businessName,
        website: params.website
      },
      signal // Pass signal to HttpRequest
    );
  }

  /**
   * Creates a Reeach registration URL with UTM parameters forwarded from the original landing page.
   * Only forwards utm_* parameters, ignoring Google auto-tagging parameters (gclid, gbraid, gad_*).
   * 
   * @param serverUrl
   * @returns
   */
  addUtmParametersToUrl(serverUrl: string): string {
    const url = new URL(serverUrl);
    
    // Forward UTM parameters from the original landing page
    const utmParams = UtmService.getUtmParams();
    
    // Add each UTM parameter to the URL (only non-empty values)
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value && value.trim()) {
        url.searchParams.set(key, value);
      }
    });
    
    return url.toString();
  }
}

export default new Api();
